import DeleteIcon from '@mui/icons-material/Delete'
import { Box, Menu, MenuItem, TextField, Typography } from '@mui/material'
import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'

import downloadIcon from '@/assets/images/Download.svg'
import editIcon from '@/assets/images/EditIcon.svg'
import Button from '@/components/Button'
import Dialog from '@/components/Dialog'
import {
  useDeleteSavedRecipeMutation,
  useUpdateRecipeNameMutation,
} from '@/store/apiService'
import { getProjectPermissions } from '@/utils/constants'

interface ActionMenuProps {
  anchorEl: null | HTMLElement
  handleMenuClose: () => void
  caseData: any
  refetch: () => void
  caseDetails?: any
}

const RecipeDetailActionMenu: React.FC<ActionMenuProps> = ({
  anchorEl,
  handleMenuClose,
  caseData,
  refetch,
  caseDetails,
}) => {
  const { t } = useTranslation('translation', { keyPrefix: 'savedRecipeTab' })
  const [deleteSaveRecipe] = useDeleteSavedRecipeMutation()
  const [updateRecipeName] = useUpdateRecipeNameMutation()

  const [editOpen, setEditOpen] = useState(false)
  const [isDeleteDialog, setIsDeleteDialog] = useState(false)
  const [caseIdToDelete, setCaseIdToDelete] = useState<string | null>(null)
  const [recipeIdToDelete, setRecipeIdToDelete] = useState<string | null>(null)

  const [form, setForm] = useState({ recipeName: '' })

  const handleEdit = () => {
    setCaseIdToDelete(caseData?.case)
    setRecipeIdToDelete(caseData?.id)
    if (caseData?.name) {
      setForm({ recipeName: caseData.name })
    }
    setEditOpen(true)
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target
    setForm((prev) => ({ ...prev, [name]: value }))
  }

  const handleUpdate = async () => {
    try {
      await updateRecipeName({
        id: recipeIdToDelete,
        name: form.recipeName,
        caseId: caseIdToDelete,
      }).unwrap()
      toast.success(t('renameDialog.renameSuccess'))
      setEditOpen(false)
      refetch()
    } catch (err) {
      toast.error(t('renameDialog.renameFailed'))
    }
  }

  const isFormValid = form.recipeName.trim() !== ''

  const role = caseDetails?.role
  const permissions = getProjectPermissions(role)

  return (
    <>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        slotProps={{
          paper: {
            sx: {
              bgcolor: 'rgba(41, 41, 41, 1)',
              color: 'white',
              mt: 1,
              boxShadow:
                '0px 3px 14px 2px rgba(0, 0, 0, 0.12), 0px 8px 10px 1px rgba(0, 0, 0, 0.14), 0px 5px 5px -3px rgba(0, 0, 0, 0.20)',
              borderRadius: '4px',
            },
          },
          list: {
            sx: {
              bgcolor: 'rgba(41, 41, 41, 1)',
            },
          },
        }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        {permissions.canDownloadRecipes && (
          <MenuItem
            onClick={() => {
              if (caseData?.recipe_pdf) {
                const link = document.createElement('a')
                link.href = caseData.recipe_pdf
                document.body.appendChild(link)
                link.click()
                link.remove()
              } else {
                toast.error(t('pdfNotAvailable'))
              }
              handleMenuClose()
            }}
            sx={{ '&:hover': { backgroundColor: '#33393E' } }}
          >
            <Box
              component="img"
              src={downloadIcon}
              alt="download"
              sx={{ width: 24, height: 24, mr: '12px' }}
            />
            {t('download')}
          </MenuItem>
        )}

        {permissions.canEditRecipes && (
          <MenuItem
            onClick={() => {
              handleMenuClose()
              handleEdit()
            }}
            sx={{ '&:hover': { backgroundColor: '#33393E' } }}
          >
            <Box
              component="img"
              src={editIcon}
              alt="Edit"
              sx={{ width: 24, height: 24, mr: '12px' }}
            />
            {t('rename')}
          </MenuItem>
        )}

        {permissions.canDeleteRecipes && (
          <MenuItem
            onClick={() => {
              setCaseIdToDelete(caseData?.case)
              setRecipeIdToDelete(caseData?.id)
              handleMenuClose()
              setIsDeleteDialog(true)
            }}
            sx={{ '&:hover': { backgroundColor: '#33393E' } }}
          >
            <DeleteIcon sx={{ mr: '12px' }} />
            {t('delete')}
          </MenuItem>
        )}
      </Menu>

      <Dialog
        isOpen={isDeleteDialog}
        onClose={() => setIsDeleteDialog(false)}
        title={t('deleteDialog.title')}
        content={
          <p>
            {t('deleteDialog.description')}
            <br />
            {t('deleteDialog.actionUndone')}
          </p>
        }
        paperStyles={{ width: '600px' }}
        actionButtons={
          <Box display="flex" justifyContent="flex-end" gap={2}>
            <Button
              variant="outlined"
              label={t('deleteDialog.cancelButton')}
              onClick={() => {
                setIsDeleteDialog(false)
                handleMenuClose()
              }}
              textTransform="uppercase"
            />
            <Button
              variant="contained"
              label={t('deleteDialog.deleteButton')}
              onClick={async () => {
                if (
                  caseIdToDelete &&
                  recipeIdToDelete &&
                  permissions.canDeleteRecipes
                ) {
                  try {
                    await deleteSaveRecipe({
                      ids: [recipeIdToDelete],
                      caseId: caseIdToDelete,
                    }).unwrap()
                    setIsDeleteDialog(false)
                    toast.success(t('deleteDialog.deleteSuccess'))
                    refetch()
                  } catch (err) {
                    toast.error(t('deleteDialog.deleteFailed'))
                  }
                }
              }}
              textTransform="uppercase"
            />
          </Box>
        }
        triggerElement={undefined}
      />

      <Dialog
        isOpen={editOpen}
        onClose={() => setEditOpen(false)}
        title={
          <Typography fontSize={24} fontWeight={500}>
            {t('renameDialog.title')}
          </Typography>
        }
        content={
          <Box display="flex" flexDirection="column" gap={2}>
            <TextField
              label={t('renameDialog.recipeName')}
              name="recipeName"
              value={form.recipeName}
              onChange={handleChange}
              fullWidth
              variant="outlined"
              size="medium"
            />
          </Box>
        }
        actionButtons={
          <Box display="flex" justifyContent="flex-end" gap={2}>
            <Button
              label={t('renameDialog.cancelButton')}
              onClick={() => setEditOpen(false)}
              variant="outlined"
              textTransform="uppercase"
            />
            <Button
              label={t('renameDialog.saveButton')}
              onClick={handleUpdate}
              variant="contained"
              disabled={!isFormValid}
              textTransform="uppercase"
            />
          </Box>
        }
        paperStyles={{ width: '800px' }}
        triggerElement={null}
      />
    </>
  )
}

export default RecipeDetailActionMenu
